import React, {FC, useEffect, useState, useRef} from "react";
import {first} from "underscore";
import parse from 'html-react-parser';
import FlyOut from "./FlyOut/FlyOut";
import {JSONConfig} from "../../types";

interface NavigationProps {
  config: JSONConfig;
  fixedSearch: any;
}

const Navigation: FC<NavigationProps> = ({config, fixedSearch}) => {
  const navRootIdMemory : string = config.active_nav_root || "";
  const [sectionData, setSectionData] = useState('');
  const [activeNavRootId, setActiveNavRootId] = useState(config.active_nav_root || "");
  const [activePageId, setActivePageId] = useState(config.active_page || "");
  const [navBreadCrumb, setNavBreadCrumb] = useState(config.flyout_breadcrumb || []);
  const [navData, setNavData] = useState<any>([]);
  const [naviOpen, setNaviOpen] = useState(false);
  const [zIndexWrapper, setZIndexWrapper] = useState(-1);
  const [flyOutTabindex, setFlyOutTabindex] = useState(-1);
  const navLinkRefs: any = useRef({});
  const closeFlyoutRef: any = useRef(null);
  const dictionary = config.dictionary || {};

  // Call the child method -> animate from right to left from FlyOut
  const callRightToLeftAnimation: any = useRef();
  const callAnimation = () => callRightToLeftAnimation.current?.();
  const focusNavLinkByNavId = (id: string) => navLinkRefs.current[id] && navLinkRefs.current[id].focus();
  // Get Navigation Data from CMS (First Spirit)
  const getNavData = async () => {
    fetch(config.url)
      .then((response) => response.json())
      .then((data) => {
        setNavData(data.root);
      })
      .catch((_error) => {
        // catch error
      });
  }

  // close flyout
  const handleClose = () => {
    setSectionData('');

    // reset active tag after closing flyout
    setActiveNavRootId(navRootIdMemory);

    // set navi to close
    setNaviOpen(false);
    setZIndexWrapper(-1);
    setFlyOutTabindex(-1);
    focusNavLinkByNavId(activeNavRootId);
  }

  // create main navigation
  const linkChildrenClick = (e: any, data: any) => {
    handleClose();
    if (naviOpen && activeNavRootId === data.id) return;
    if (data.children.length === 1 && data.children[0]?.isOverview) {
      window.location.href = data.url;
      return;
    }

    // catch click
    e.preventDefault();

    // set active element
    setActiveNavRootId(data.id);

    // clone data to force re-render
    setSectionData({...data});

    // call the animation method
    callAnimation();

    // set navi to open
    setNaviOpen(true);
    setZIndexWrapper(1);
    setFlyOutTabindex(0);
  }

  // handle 'ESC' Key press
  const handleEsc = (event: any): void => {
    if (event.keyCode === 27 && naviOpen) {
      handleClose();
    }
  };

  // check if
  const checkIfClickedOutside = (event: any) => {
    if (!document.querySelector('#main-navigation .flyout-navigation')?.contains(event.target) && naviOpen) {
      event.preventDefault();
      handleClose();
    }
  };

  const closeFlyoutAndMoveFocusToNavigation = (event :KeyboardEvent) => {
    if (event.key === 'Tab' && !event.shiftKey && document.activeElement === closeFlyoutRef.current) {
      handleClose();
    }
  }

  //
  useEffect(() => {
    // get Navigation JSON via AJAX Call
    getNavData();
    // add close listener
    window.addEventListener('keydown', handleEsc);
    document.addEventListener("mousedown", checkIfClickedOutside);
    document.addEventListener('keydown', closeFlyoutAndMoveFocusToNavigation);
    // @ts-ignore
    if (window.KfWModules !== undefined && window.KfWModules !== null) {
      // @ts-ignore
      window.KfWModules.initializeModule('fixed-search', $('.fixed-search[data-module="fixed-search"]'));
    }
    // cleanup the event listener to prevent multiple calls
    return () => {
      document.removeEventListener("mousedown", checkIfClickedOutside);
      window.removeEventListener("keydown", handleEsc);
      document.removeEventListener('keydown', closeFlyoutAndMoveFocusToNavigation);
    }
  }, [sectionData]);

  return (
    <>
      {navData ? (
        <>
          <div className="flyout-navigation">
            <div
              className="nav-primary__wrapper js-nav-primary-wrapper nav-primary-overlap"
              z-index={zIndexWrapper}
            >
              <nav className="nav-primary l-container" role="navigation" id="primary-nav">
                <ul>
                  {navData.map((data: any) => {
                    return (
                      <li key={data.id}>
                        <a ref={(e) => {
                          navLinkRefs.current[data.id] = e
                        }}
                          key={data.id} href="#"
                           className={`${(activeNavRootId === data.id) ? "active " : ""}nav-primary__link js-nav-primary-link`}
                           data-section="main-navi_main"
                           data-action="toggle-nav-primary-panel"
                           role={data.children.length === 1 ? "link" : "button"}
                           data-id={data.id}
                           id={data.id}
                           aria-expanded={activeNavRootId === data.id && naviOpen ? "true" : "false"}
                           onClick={(event) => linkChildrenClick(event, data)}>
                          {data.headline}
                        </a>
                        {activeNavRootId === data.id &&
                          <FlyOut
                            linkChildren={sectionData}
                            crumbItem={sectionData}
                            handleClose={handleClose}
                            navData={navData}
                            callRightToLeftAnimation={callRightToLeftAnimation}
                            naviOpen={naviOpen}
                            activePageId={activePageId}
                            activeNavRootId={activeNavRootId}
                            navPathIds={navBreadCrumb}
                            tabIndex={flyOutTabindex}
                            closeFlyoutRef={closeFlyoutRef}
                            dictionary={dictionary}
                          />
                        }
                      </li>
                    );
                  })
                  }
                </ul>
                {parse(fixedSearch)}
              </nav>
            </div>
          </div>
        </>
      ) : (<div/>)}
    </>
  )
}

export default Navigation;
